import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useEntities, useInvalidate } from "@/lib/queries";
import { useGlyphs, GlyphComposer } from "@/components/language/Script";
import { HostTips, SafeImg } from "@/components/ImageGallery";

export const POS = ["noun", "verb", "adjective", "adverb", "pronoun", "particle", "phrase", "other"];
const EMPTY = { word: "", pronunciation: "", meaning: "", part_of_speech: "", example: "", notes: "", entity_id: null, image_url: "", glyph_ids: [] };

export function WordDialog({ lang, initial, onOpenChange }) {
  const invalidate = useInvalidate();
  const { data: entities = [] } = useEntities(lang.universe_id);
  const { data: glyphs = [] } = useGlyphs(lang.id);
  const [form, setForm] = useState({ ...EMPTY, ...(initial || {}) });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, form[k] ?? (k === "entity_id" ? null : "")]));
    try {
      if (initial?.id) await api.patch(`/words/${initial.id}`, body);
      else await api.post(`/languages/${lang.id}/words`, body);
      invalidate(["words", lang.id], ["language", lang.id], ["languages", lang.universe_id]);
      toast.success(initial?.id ? "Word updated" : `${form.word} added`);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="word-dialog">
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle className="font-display text-3xl">{initial?.id ? `Edit ${initial.word}` : `New ${lang.name} word`}</DialogTitle>
            <DialogDescription>A dictionary entry in {lang.name}.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-5">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Word</Label><Input value={form.word} onChange={set("word")} required className="font-display text-xl" data-testid="word-form-word" /></div>
              <div className="space-y-2"><Label>Pronunciation</Label><Input value={form.pronunciation} onChange={set("pronunciation")} placeholder="KESH" data-testid="word-form-pronunciation" /></div>
            </div>
            <div className="grid grid-cols-[1fr_180px] gap-3">
              <div className="space-y-2"><Label>Meaning</Label><Input value={form.meaning} onChange={set("meaning")} data-testid="word-form-meaning" /></div>
              <div className="space-y-2">
                <Label>Part of speech</Label>
                <Select value={form.part_of_speech || "none"} onValueChange={(v) => set("part_of_speech")(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="word-form-pos"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{POS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Example sentence</Label><Input value={form.example} onChange={set("example")} data-testid="word-form-example" /></div>
            <div className="space-y-2"><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={set("notes")} data-testid="word-form-notes" /></div>
            <div className="space-y-2"><Label>Written in {lang.name}'s script</Label><GlyphComposer value={form.glyph_ids || []} onChange={set("glyph_ids")} glyphs={glyphs} /></div>
            <div className="space-y-2">
              <div className="flex items-center justify-between"><Label>Image link (optional)</Label><HostTips /></div>
              <div className="flex items-center gap-2">
                <Input value={form.image_url || ""} onChange={set("image_url")} placeholder="https://i.imgur.com/abc123.jpg" data-testid="word-form-image" />
                {form.image_url && <SafeImg src={form.image_url} className="h-10 w-10 shrink-0 rounded" />}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Linked lore entry</Label>
              <Select value={form.entity_id || "none"} onValueChange={(v) => set("entity_id")(v === "none" ? null : v)}>
                <SelectTrigger data-testid="word-form-entity"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button type="submit" disabled={busy || !form.word.trim()} data-testid="word-form-save">{busy ? "Saving…" : "Save word"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
